/**
 * The socket, and nothing but.
 *
 * Colyseus lives behind this file and nowhere else, so the rest of the client
 * deals in protocol types. That is the same discipline the server keeps in
 * `WorldRoom`, for the same reason and with the same benefit: everything
 * upstream of here is testable without a network.
 *
 * The client speaks its protocol version on join and is refused if it is
 * stale (M0-03). Refusing is the *point* — the alternative is a tab somebody
 * left open last week silently mis-parsing snapshots.
 */

import {
  CLIENT_MESSAGE,
  PROTOCOL_VERSION,
  SERVER_MESSAGE,
  type ClientIntentMessage,
  type IntentType,
  type LogMessage,
  type RejectedMessage,
  type SnapshotMessage,
  type WelcomeMessage,
} from '@annwn/protocol';
import { Client, type Room } from 'colyseus.js';

import { loadAccessToken, loadPlayerId, savePlayerId } from './identity.js';

export interface ConnectionHandlers {
  onWelcome(message: WelcomeMessage): void;
  onSnapshot(message: SnapshotMessage): void;
  onRejected(message: RejectedMessage): void;
  onLog(message: LogMessage): void;
  onError(error: Error): void;
  onClose(): void;
}

export interface ConnectOptions {
  readonly url: string;
  readonly name: string;

  /**
   * The background chosen at creation (M7-03), or `null`/absent for a returning
   * character or a guest. Sent under `backgroundId`; the server honours it only
   * when it is seating a brand-new character and ignores it otherwise, so there
   * is no harm in a returning player's client leaving it unset.
   */
  readonly backgroundId?: string | null;
}

export class Connection {
  private room: Room | null = null;

  /** Monotonic per connection. Comes back on the snapshot's `ack`. */
  private sequence = 0;

  constructor(private readonly handlers: ConnectionHandlers) {}

  get connected(): boolean {
    return this.room !== null;
  }

  async connect(options: ConnectOptions): Promise<void> {
    const client = new Client(options.url);

    // A signed-in token if the portal stored one (M7-01/M7-02), otherwise
    // `null`. Sent under `token`, which an authed world verifies and seats the
    // join on the account's player — overriding `playerId` below. A guest sends
    // no token and joins on the browser's id, exactly as before.
    const token = loadAccessToken();

    const room = await client.joinOrCreate('world', {
      protocolVersion: PROTOCOL_VERSION,
      name: options.name,
      // Who we were last time, or `null` on a first visit. The server mints one
      // in that case and returns it in the welcome, which is where it gets
      // written down — see `onWelcome` below.
      playerId: loadPlayerId(),
      ...(token ? { token } : {}),
      ...(options.backgroundId ? { backgroundId: options.backgroundId } : {}),
    });

    this.room = room;

    room.onMessage(SERVER_MESSAGE.welcome, (message: WelcomeMessage) => {
      // Store what the server confirmed rather than what we sent. It is the
      // same value in the ordinary case and the *right* value in the two that
      // are not: a first visit, and a stored id that had been mangled.
      savePlayerId(message.playerId);
      this.handlers.onWelcome(message);
    });
    room.onMessage(SERVER_MESSAGE.snapshot, (message: SnapshotMessage) =>
      this.handlers.onSnapshot(message),
    );
    room.onMessage(SERVER_MESSAGE.rejected, (message: RejectedMessage) => {
      console.warn('[look-debug] transport received rejected intent', message);
      this.handlers.onRejected(message);
    });
    room.onMessage(SERVER_MESSAGE.log, (message: LogMessage) => {
      const examined = message.events.filter((event) => event.type === 'examined');
      if (examined.length > 0) {
        console.info('[look-debug] transport received examined log', { events: examined });
      }
      this.handlers.onLog(message);
    });

    room.onError((code, message) =>
      this.handlers.onError(new Error(`server error ${code}: ${message ?? 'no detail'}`)),
    );
    room.onLeave(() => {
      this.room = null;
      this.handlers.onClose();
    });
  }

  /** The next sequence number, claimed before the intent is built. */
  nextSequence(): number {
    this.sequence += 1;
    return this.sequence;
  }

  send(type: IntentType, payload: unknown, seq: number): void {
    if (type === 'examine') {
      console.info('[look-debug] transport examine send', {
        connected: this.room !== null,
        seq,
        payload,
      });
    }
    if (!this.room) return;
    const message: ClientIntentMessage = { seq, type, payload };
    this.room.send(CLIENT_MESSAGE.intent, message);
  }

  async disconnect(): Promise<void> {
    await this.room?.leave(true);
    this.room = null;
  }
}
